import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { LogoWinarq, LogoIcono } from "./LogoWinarq";

const G = {
  bg:"#FAF9F7", s1:"#FFFFFF", s2:"#F4F2EF",
  border:"#E2DFD9", border2:"#D4D0CA",
  black:"#1C1917", off:"#3D3830", muted:"#8C8078", dim:"#C8C0B8",
  orange:"#D2611A",
  sans:"'Outfit','Helvetica Neue',Arial,sans-serif",
  mono:"'IBM Plex Mono',monospace",
};
const O = "#D2611A";

const MENU_ADMIN = [
  { id:"dashboard",         label:"Dashboard",              icon:"◈" },
  { id:"productos",         label:"Productos",              icon:"▦" },
  { id:"categorias",        label:"Categorías / Landing",   icon:"◻" },
  { id:"ciclos",            label:"Ciclos / Contenedores",  icon:"◎" },
  { id:"pedidos",           label:"Pedidos",                icon:"▸" },
  { id:"pedidos-proveedor", label:"Pedidos x proveedor",    icon:"⊕" },
  { id:"proveedores",       label:"Proveedores",            icon:"◉" },
  { id:"catalogo-pdf",      label:"Catálogo PDF",           icon:"▤" },
  { id:"documentos",        label:"Documentos",             icon:"▱" },
  { id:"usuarios",          label:"Usuarios",               icon:"◇" },
  { id:"globales",          label:"Variables globales",     icon:"⬡" },
  { id:"notificaciones",    label:"Notificaciones",         icon:"◆" },
];

const MENU_VENDEDOR = [
  { id:"mis-clientes", label:"Mis clientes",  icon:"◇" },
  { id:"pedidos",      label:"Pedidos",       icon:"▸" },
  { id:"comisiones",   label:"Comisiones",    icon:"◉" },
];

const MENU_COMPRADOR = [
  { id:"catalogo",    label:"Catálogo",     icon:"▦" },
  { id:"mis-pedidos", label:"Mis pedidos",  icon:"▸" },
  { id:"amedida",     label:"A medida",     icon:"◎" },
];

export default function Layout({ children, activeView, setActiveView }) {
  const { profile, signOut } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const rol = profile?.rol || "comprador";

  const menu = rol === "admin" ? MENU_ADMIN : rol === "vendedor" ? MENU_VENDEDOR : MENU_COMPRADOR;

  const ROL_LABEL = { admin:"Administrador", vendedor:"Vendedor", comprador:"Comprador" };
  const ROL_COLOR = { admin:O, vendedor:"#2563EB", comprador:"#16A34A" };

  return (
    <div style={{ display:"flex", minHeight:"100vh", background:G.bg, fontFamily:G.sans }}>

      {/* Sidebar */}
      <div style={{
        width: collapsed ? 56 : 220,
        flexShrink: 0,
        background: G.s1,
        borderRight: `1.5px solid ${G.border}`,
        display: "flex",
        flexDirection: "column",
        transition: "width .25s ease",
        overflow: "hidden",
        position: "sticky",
        top: 0,
        height: "100vh",
      }}>

        {/* Logo — click va a landing */}
        <div onClick={() => window.location.href = "/"}
          style={{ height:60, display:"flex", alignItems:"center", padding:collapsed?"0 10px":"0 14px", borderBottom:`1.5px solid ${G.border}`, flexShrink:0, cursor:"pointer" }}>
          {collapsed ? <LogoIcono size={30}/> : <LogoWinarq height={34} dark={true}/>}
        </div>

        {/* Nav */}
        <nav style={{ flex:1, overflowY:"auto", padding:"10px 0" }}>
          {menu.map(item => {
            const active = activeView === item.id;
            return (
              <button key={item.id} onClick={() => setActiveView(item.id)}
                style={{
                  width: "100%", display:"flex", alignItems:"center",
                  gap:10, padding: collapsed ? "10px 0" : "9px 14px",
                  justifyContent: collapsed ? "center" : "flex-start",
                  background: active ? "#FEF3E8" : "transparent",
                  borderLeft: active ? `3px solid ${O}` : "3px solid transparent",
                  border:"none", cursor:"pointer",
                  fontFamily: G.sans, fontSize:12, fontWeight: active ? 700 : 400,
                  color: active ? O : G.off,
                  transition:"all .15s",
                }}>
                <span style={{ fontSize:14, color:active?O:G.muted, flexShrink:0 }}>{item.icon}</span>
                {!collapsed && <span style={{ whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Footer sidebar */}
        <div style={{ borderTop:`1px solid ${G.border}`, padding: collapsed?"10px 0":"12px 14px" }}>
          {!collapsed && (
            <div style={{ marginBottom:10 }}>
              <div style={{ fontFamily:G.sans, fontSize:9, letterSpacing:1.5, textTransform:"uppercase", color:G.muted, marginBottom:2, fontWeight:600 }}>Usuario activo</div>
              <div style={{ fontFamily:G.sans, fontSize:12, color:G.black, fontWeight:700 }}>{profile?.nombre || profile?.email?.split("@")[0] || "—"}</div>
              <div style={{ fontFamily:G.mono, fontSize:8, color:ROL_COLOR[rol], marginTop:1, textTransform:"uppercase", letterSpacing:1 }}>{ROL_LABEL[rol]}</div>
            </div>
          )}
          <button onClick={signOut}
            style={{ width:"100%", padding: collapsed?"8px 0":"7px 0", background:"transparent", border:`1px solid ${G.border}`, cursor:"pointer", fontFamily:G.sans, fontSize:9, letterSpacing:1.5, textTransform:"uppercase", color:G.muted, fontWeight:500 }}>
            {collapsed ? "↩" : "Cerrar sesión"}
          </button>
        </div>

        {/* Toggle collapse */}
        <button onClick={() => setCollapsed(c => !c)}
          id="sidebar-toggle"
          style={{ position:"absolute", bottom:60, right:-12, width:22, height:22, borderRadius:"50%", background:G.s1, border:`1.5px solid ${G.border}`, cursor:"pointer", fontSize:10, color:G.muted, display:"flex", alignItems:"center", justifyContent:"center", zIndex:10 }}>
          {collapsed ? "›" : "‹"}
        </button>
      </div>

      {/* Main */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0, overflow:"hidden" }}>
        {/* Topbar */}
        <div style={{ height:48, borderBottom:`1px solid ${G.border}`, display:"flex", alignItems:"center", padding:"0 24px", gap:16, background:G.s1, flexShrink:0 }}>
          <div style={{ fontFamily:G.sans, fontSize:13, color:G.muted, fontWeight:400 }}>
            {menu.find(m => m.id === activeView)?.label || "Panel"}
          </div>
          <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ fontFamily:G.mono, fontSize:9, color:G.muted }}>
              {new Date().toLocaleDateString("es-AR",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
            </div>
            <div style={{ fontFamily:G.mono, fontSize:8, color:ROL_COLOR[rol], border:`1px solid ${ROL_COLOR[rol]}`, padding:"2px 8px", letterSpacing:1.5, textTransform:"uppercase", fontWeight:700 }}>
              {ROL_LABEL[rol]}
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex:1, overflowY:"auto", padding:24 }}>
          {children}
        </div>
      </div>
    </div>
  );
}
